import { Users } from '@platform/client';
import { Button, InlineFormInput } from '@platform/components';
import { AxiosError } from 'axios';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { SetPasswordInput } from '../components/SetPasswordInput';
import { AuthedPage, HeaderFooterLayout } from '../../app';

type NewPasswordFormValues = {
  password: string;
  passwordConfirmation: string;
};

type ConfirmPasswordFormValues = {
  password: string;
};

export const ChangePasswordPage = () => {
  const [passwordConfirmed, setPasswordConfirmed] = useState(false);
  const [passwordReset, setPasswordReset] = useState(false);

  return (
    <AuthedPage>
      <HeaderFooterLayout>
        <div className='w-full h-full flex flex-col gap-8 md:justify-center items-center p-4 overflow-y-scroll'>
          <h2 className='text-display-sm md:text-display-md'>
            {passwordReset ? 'Success! Your password has been updated' : 'Update your password'}
          </h2>
          {!passwordConfirmed && !passwordReset && (
            <ConfirmPasswordSegment setPasswordConfirmed={setPasswordConfirmed} />
          )}
          {passwordConfirmed && !passwordReset && (
            <NewPasswordSegment setPasswordReset={setPasswordReset} />
          )}
          {passwordReset && <PasswordResetConfirmationSegment />}
          <a className='underline text-brand-500' href='mailto:match@movingworlds.org'>
            Email us for help
          </a>
        </div>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};

type ConfirmPasswordSegmentProps = {
  setPasswordConfirmed: (value: boolean) => void;
};

const ConfirmPasswordSegment = (props: ConfirmPasswordSegmentProps) => {
  const { setPasswordConfirmed } = props;
  const [invalidCredentials, setInvalidCredentials] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ConfirmPasswordFormValues>({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const confirmPasswordMutation = Users.useConfirmPassword({
    mutation: {
      onSuccess: () => {
        setPasswordConfirmed(true);
      },
      onError: (error) => {
        if ((error as AxiosError).status === 401) {
          setPasswordConfirmed(false);
          setInvalidCredentials(true);
        }
      },
    },
  });

  const confirmPassword: SubmitHandler<ConfirmPasswordFormValues> = async (data) => {
    confirmPasswordMutation.mutate({
      data: {
        password: data.password,
      },
    });
  };

  return (
    <>
      <div className='w-full md:w-[514px] text-lg text-center overflow-wrap'>
        Enter your current password to get started
      </div>
      <form
        className='flex flex-col gap-8 w-full md:w-[514px] max-w-[514px] items-center'
        name='login'
        onSubmit={handleSubmit(confirmPassword)}
      >
        <InlineFormInput
          label='Password'
          inputType='password'
          autocomplete='current-password'
          errors={errors.password}
          placeholder='Enter the password you created'
          register={() => {
            return register('password', {
              required: { value: true, message: 'Please enter your password.' },
            });
          }}
        />
        {invalidCredentials && (
          <div className=' w-full border border-gray-200 bg-error-25 shadows-xs h-[52px] flex gap-1 items-center p-4 text-sm'>
            Password is incorrect.
          </div>
        )}
        <Button type='submit' variant='primary' size='md' additionalClassnames='w-[107px]'>
          Continue
        </Button>
      </form>
    </>
  );
};

type NewPasswordSegmentProps = {
  setPasswordReset: (value: boolean) => void;
};

const NewPasswordSegment = (props: NewPasswordSegmentProps) => {
  const { setPasswordReset } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<NewPasswordFormValues>({ mode: 'onBlur', reValidateMode: 'onBlur' });

  const changePasswordMutation = Users.useChangePassword({
    mutation: {
      onSuccess: () => {
        setPasswordReset(true);
      },
      onError: (error) => {
        if ((error as AxiosError).status === 401) {
          setPasswordReset(false);
        }
      },
    },
  });

  const changePassword: SubmitHandler<NewPasswordFormValues> = async (data) => {
    changePasswordMutation.mutate({
      data: {
        newPassword: data.password,
      },
    });
  };

  const firstPassword = watch('password');
  const secondPassword = watch('passwordConfirmation');

  const passwordsEntered = !!firstPassword && !!secondPassword;
  const passwordsMatch = firstPassword === secondPassword;

  return (
    <>
      <div className='w-full md:w-[514px] text-lg text-center overflow-wrap'>
        Create a new password that meets the requirements.
      </div>
      <form
        className='flex flex-col gap-8 w-full md:w-[514px] max-w-[514px] items-center'
        name='login'
        onSubmit={handleSubmit(changePassword)}
      >
        <SetPasswordInput
          password={firstPassword || ''}
          register={register}
          errors={errors.password}
          label={'New password'}
          placeholder='Enter a new password'
        />
        <div className='flex flex-col gap-2 '>
          <SetPasswordInput
            password={secondPassword || ''}
            register={register}
            errors={errors.passwordConfirmation}
            hideRequirements={true}
            registerKey='passwordConfirmation'
            label={'Re-enter password'}
            placeholder='Re-enter your new password'
          />
          {passwordsEntered && !passwordsMatch && (
            <div className='text-sm text-error-500 overflow-wrap'>Passwords must match</div>
          )}
        </div>
        <Button
          isDisabled={!passwordsEntered || !passwordsMatch}
          type='submit'
          variant='primary'
          size='md'
          additionalClassnames='w-[160px]'
        >
          Reset password
        </Button>
      </form>
    </>
  );
};

const PasswordResetConfirmationSegment = () => {
  return (
    <Button href='/platform/dashboard' variant='primary' size='md' additionalClassnames='w-[160px]'>
      Return to dashboard
    </Button>
  );
};
